import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * useReturnableRoute — helper for list→detail→back flows.
 *
 * Wraps `navigate(to, { state })` so that:
 *   1. The current URL (path + query string) is captured into
 *      `state.from` under the hood.
 *   2. Any extra state fields the caller wants (e.g. `{ scan }`) are
 *      passed through unchanged.
 *
 * The detail page then does `navigate(state.from)` on its back arrow —
 * which restores the caller's filters, pagination, mode, etc.
 *
 * This centralizes the "always send full pathname+search as `from`"
 * convention. Bugs like `Duplicates.jsx` hardcoding `from: "/duplicates"`
 * and dropping `?mode=species` on the round trip are prevented by design.
 *
 * Usage:
 *   const openDetail = useReturnableRoute();
 *   openDetail(`/plant/${id}`, { scan });
 */
export function useReturnableRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (to, extraState = {}) => {
      const from = `${location.pathname}${location.search}`;
      navigate(to, { state: { ...extraState, from } });
    },
    [navigate, location.pathname, location.search]
  );
}
